import { Component, ElementRef, inject, OnInit, ViewChild } from '@angular/core';
import { ZzzConfigService } from 'src/app/zzz/core/config/config.service';
import { AbstractControl, FormGroup, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, ValidationErrors, Validators } from '@angular/forms';
import { BehaviorSubject } from 'rxjs';
import { Feature, GEOMETRY_FORM_FIELD_DIRECTIVES, GeometryFormFieldInputComponent, IgoGeometryFormFieldModule, IgoMap } from '@igo2/geo';
import { IgoAppGeometryFormModule, MapState } from '@igo2/integration';
import { HttpClient } from '@angular/common/http';
import { CommentairePlansTravailService } from 'src/app/shared/services/commentaire-plans-travail.service';
import { DomainesService } from 'src/app/shared/services/domaines.service';
import { ValeurDomaineDto } from 'src/app/shared/models/domaines.dto';
import { SigtUtilisateurService } from 'src/app/shared/services/sigt-utilisateur.service';
import { Constantes } from 'src/app/shared/constantes';
import { ToolComponent } from '@igo2/common/tool';
import { MessageService } from '@igo2/core/message';
import { LanguageService } from '@igo2/core/language';
import { TranslateModule } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import type { Type } from 'ol/geom/Geometry';
import type { default as OlGeometry } from 'ol/geom/Geometry';
import * as olStyle from 'ol/style';
import OlFeature from 'ol/Feature';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';

@ToolComponent({
  name: 'sigt-commenter-plan-travail',
  title: 'igo.integration.tools.sigtCommentePlanTravail',
  icon: 'chat'
})
@Component({
    selector: 'app-sigt-commenter-plan-travail',
    templateUrl: './sigt-commenter-plan-travail.component.html',
    styleUrls: ['./sigt-commenter-plan-travail.component.scss'],
    imports: [
        CommonModule,
        ReactiveFormsModule,
        TranslateModule,
        MatButtonModule,
        MatFormFieldModule,
        MatIconModule,
        MatSelectModule,
        MatInputModule,
        GEOMETRY_FORM_FIELD_DIRECTIVES
    ]
})
export class SigtCommenterPlanTravailComponent implements OnInit {
  @ViewChild('commentaire') commentaireElement: ElementRef;

  form: UntypedFormGroup;
  messageApplication: { [key: string]: string } = {};
  nom = new BehaviorSubject<string>("");
  courriel = new BehaviorSubject<string>("");
  listeTypeDePlan: ValeurDomaineDto[];
  geometryType: Type = 'Point';
  radius: number = 1000;
  drawStyle$: BehaviorSubject<olStyle.Style | ((feature, resolution) => olStyle.Style)> = new BehaviorSubject(undefined);
  valeurTypePlanDefaut: string;

  get map(): IgoMap {
    return this.mapState.map;
  }

  // TerrAPI du MSI, par la route /terrapi de la passerelle SIGT (l'adresse
  // geoegl.msp.gouv.qc.ca/apis/terrapi cesse de fonctionner le 2027-01-27).
  readonly #urlTerrApi = inject(ZzzConfigService).get().authApiEndPoint + '/terrapi';

  constructor(
    private formBuilder: UntypedFormBuilder,
    private utilisateurService: SigtUtilisateurService,
    private mapState: MapState,
    private messageService: MessageService,
    private languageService: LanguageService,
    private httpClient: HttpClient,
    private commentairePlansTravailService: CommentairePlansTravailService,
    private domainesService: DomainesService
  ) { }

  ngOnInit(): void {
    this.form = this.formBuilder.group({
      positionSurCarte: ["", [Validators.required]],
      positionSurCarteForViewOnly: ["", [Validators.required]],
      codeRegionAdministrative: ["", [Validators.required]],
      utilisateurId: [this.utilisateurService.current.id, [Validators.required]],
      commentaire: ["", [Validators.required, Validators.minLength(10), this.validerPattern]],
      typePlan: ["", [Validators.required]]
    }, { validators: [this.validerChamps.bind(this)] });

    this.nom.next(this.utilisateurService.current.nom + ' ' + this.utilisateurService.current.prenom);
    this.courriel.next(this.utilisateurService.current.courriel);

    this.messageApplication.echecOperation = Constantes.MessageApplicatif.OperationNonComplete_11E;
    this.messageApplication.succesOperation = Constantes.MessageApplicatif.ElementEnregistrer_7I;
    this.messageApplication.invalideCommentaireCourt = Constantes.MessageApplicatif.CommentaireAuMoins10_20E;
    this.messageApplication.invalidePointSurCarte = Constantes.MessageApplicatif.Information_22E.replace("{0}",
      this.languageService.translate.instant('sigt.integration.commenterPlanTravail.form.messagesPlaceholders.invalidePointSurCarte'));
    this.messageApplication.invalideCommentairePattern = Constantes.MessageApplicatif.Invalide_2E.replace("{0}",
      this.languageService.translate.instant('sigt.integration.commenterPlanTravail.form.messagesPlaceholders.invalideCommentairePattern'));
    this.messageApplication.obligatoireCommentaire = Constantes.MessageApplicatif.ValeurObligatoire_1E.replace("{0}",
      this.languageService.translate.instant('sigt.integration.commenterPlanTravail.form.commentaire.erreurCommentaire'));

    this.form.controls.positionSurCarte.valueChanges.subscribe(positionSurCarte => {
      if (positionSurCarte) {
        this.httpClient.get<{ features: Feature[] }>(`${this.#urlTerrApi}/locate?type=regadmin&loc=${positionSurCarte.coordinates.join(",")}`).subscribe(features => {
          if (features.features.length > 0) {
            this.form.patchValue({
              codeRegionAdministrative: features.features[0].properties.code,
              positionSurCarteForViewOnly: `${positionSurCarte.coordinates[0]}, ${positionSurCarte.coordinates[1]}`
            });
          } else {
            this.messageService.error(this.messageApplication.invalidePointSurCarte);
            this.form.patchValue({
              positionSurCarte: '',
              codeRegionAdministrative: '',
              positionSurCarteForViewOnly: ''
            });
          }

          this.commentaireElement.nativeElement.focus(); //cette ligne est nécessaire. Sans ce focus, bien que le formulaire soit valide, le bouton soumettre ne va pas etre activer
        });
      }
    });

    this.initialiserGeometryFieldInput();

    this.domainesService.obtenirValeurs("TYPE_PLAN")
      .subscribe({
        next: (data) => {
          this.listeTypeDePlan = data.valeursDomaine;
          if (this.listeTypeDePlan.length > 0) {
            this.valeurTypePlanDefaut = this.listeTypeDePlan[0].valeur;
            this.form.patchValue({ typePlan: this.valeurTypePlanDefaut });
          }
        },
        error: (error) => {
          console.log("Erreur: " + JSON.stringify(error));
        },
      }
      );
  }

  submitForm() {
    if (this.form.valid) {
      const formValue = this.form.value;

      this.commentairePlansTravailService.creerCommentaire({
        commentaire: formValue.commentaire,
        geometrieX: formValue.positionSurCarte.coordinates[0],
        geometrieY: formValue.positionSurCarte.coordinates[1],
        noRegionAdmin: formValue.codeRegionAdministrative,
        typePlan: formValue.typePlan
      }).subscribe({
        next: rep => {
          this.messageService.info(this.messageApplication.succesOperation);

          this.initialiserChamps();
        },
        error: _err => {
          this.messageService.error(this.messageApplication.echecOperation);
        }
      });
    }
  }

  validerPattern(ctrl: AbstractControl): ValidationErrors | null {
    if (ctrl.value?.length >= 10 //pour éviter de montrer une erreur avant de saisir le minimum des caracteres
      && ctrl.value.match(Constantes.regexValidationCommentaire) === null) {
      return {
        pattern: true
      };
    }
    return null;
  }

  initialiserGeometryFieldInput() {
    const pointStyle = (
      _feature: OlFeature<OlGeometry>,
      _resolution: number
    ) => {
      return new olStyle.Style({
        image: new olStyle.Circle({
          radius: 5,
          stroke: new olStyle.Stroke({
            width: 2,
            color: 'rgba(0, 153, 255)'
          }),
          fill: new olStyle.Fill({
            color: 'rgba(0, 153, 255, 0.2)'
          })
        })
      });
    };

    this.drawStyle$.next(pointStyle);
  }

  initialiserChamps(){
    this.form.patchValue({
      typePlan: this.valeurTypePlanDefaut,
      positionSurCarteForViewOnly: '',
      commentaire: '',
      positionSurCarte: '',
      codeRegionAdministrative: ''
    });
    this.form.get('positionSurCarteForViewOnly').setErrors(null,{emitEvent: false});
    this.form.get('commentaire').setErrors(null,{emitEvent: false});
  }

  validerChamps(control: FormGroup): any {
    if (control.value.positionSurCarteForViewOnly || control.value.commentaire || control.value.typePlan) {
      const typePlan = control.controls["typePlan"].value;
      const positionSurCarteForViewOnly = control.controls["positionSurCarteForViewOnly"].value;
      const commentaire = control.controls["commentaire"].value;

      if ((typePlan ===null || typePlan?.length === 0) || (commentaire ===null || commentaire?.length === 0)
        || (positionSurCarteForViewOnly ===null || positionSurCarteForViewOnly?.length === 0)){
        return { notValid: true };
      }

      return null;
    }

    return { notValid: true };
  }

  initialiserPositionCarte(){
    this.form.patchValue({
      positionSurCarte: '',
      positionSurCarteForViewOnly: ''
    });
  }
}
